import { isNil } from 'lodash';
// Import Types
import type {
    AvailableDeliveryMethodItem,
    BonusCalculateAction,
    BonusObject,
    ClientFieldItem,
    ConfigurationColumnData,
    CurrencyRates,
    DataWithPaging,
    DownloadFileResponse,
    NFTVoucherCollectionItem,
    ObjectCategoryTagItem,
    PartnerCustomField,
    PartnerLanguage,
    PartnerProvider,
    PartnerSettings,
    PopUpComparisonItem,
    PushNotificationSettings,
    SegmentsSuitableKpiConfig,
    SpinExchangeRate,
    SubscriptionPlan,
    UserInfo,
} from 'types';
// Import Configs
import {
    CRMRequestService,
    CRMGalleryRequestService,
    getCancelableRequest,
    postCancelableRequest,
    deleteCancelableRequest,
} from './http.config';
// Import Helpers
import { combineUrlParams, generateCleanUrlParams } from 'services/Helpers';

const UtilsHttpService = {
    getTranslations: () => {
        return CRMRequestService.get('/Translations/GetTranslations');
    },
    getTranslationsVersion: () => {
        return CRMRequestService.get('/Translations/GetVersion');
    },
    getClientFields: () => {
        return getCancelableRequest<ClientFieldItem[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/ClientFields',
        });
    },
    getChannelsList: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/Channels',
        });
    },
    getCurrencyRates: () => {
        return getCancelableRequest<CurrencyRates>({
            requestService: CRMRequestService,
            endpointPath: () => 'OptionsList/GetCurrencyRates',
        });
    },
    getBonusCalculateActions: () => {
        return getCancelableRequest<BonusCalculateAction[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/BonusCalculateActions',
        });
    },
    getBonusObjectsByType: () => {
        return getCancelableRequest<BonusObject[]>({
            requestService: CRMRequestService,
            endpointPath: ({ type }) => `/OptionsList/BonusObjects?type=${type}`,
        });
    },
    getSegmentKpisByIdes: () => {
        return getCancelableRequest<SegmentsSuitableKpiConfig[]>({
            requestService: CRMRequestService,
            endpointPath: ({ Ides }) =>
                `/OptionsList/GetKpiConfigSuitableSegments${combineUrlParams({ Ides: Ides.split('%2C') })}`,
        });
    },
    getTagsList: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ targetType }) => `/ObjectCategory/GetTagsList?targetType=${targetType}`,
        });
    },
    tagsList: () => {
        return postCancelableRequest<DataWithPaging<ObjectCategoryTagItem>>({
            requestService: CRMRequestService,
            endpointPath: () => '/ObjectCategory/TagsList',
        });
    },
    createTag: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/ObjectCategory/CreateTag',
        });
    },
    createObjectTag: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/ObjectCategory/CreateObjectTag',
        });
    },
    updateTag: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/ObjectCategory/UpdateTag',
        });
    },
    deleteTag: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ id }) => `/ObjectCategory/DeleteTag?tagId=${id}`,
        });
    },
    getMenuItemsById: () => {
        return getCancelableRequest<AnyObject[]>({
            requestService: CRMRequestService,
            endpointPath: ({ id, params }) => {
                return `/OptionsList/GetMenuItemsById?id=${id}${params ? '&' + params : ''}`;
            },
        });
    },
    getMenuItems: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ id, params }) => {
                return `/OptionsList/GetMenuItems${combineUrlParams({ menuItemId: id, ...params })}`;
            },
        });
    },
    getMenuItemsConfig: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => {
                return `/OptionsList/GetMenuItemsConfig`;
            },
        });
    },
    getObjectDependencies: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ objectId, dependencyType }) =>
                `/Validation/GetObjectDependencies?objectId=${objectId}&dependencyType=${dependencyType}`,
        });
    },
    getPartnerLanguage: () => {
        return getCancelableRequest<PartnerLanguage[]>({
            requestService: CRMRequestService,
            endpointPath: () => `/OptionsList/GetPartnerLanguages`,
        });
    },
    getAllKPIs: () => {
        return getCancelableRequest<ConfigurationColumnData[]>({
            requestService: CRMRequestService,
            endpointPath: () => `/Configuration/Columns`,
        });
    },
    getPartnerProviders: () => {
        return getCancelableRequest<PartnerProvider[]>({
            requestService: CRMRequestService,
            endpointPath: () => `/OptionsList/GetPartnerProviders`,
        });
    },
    getDataTableConfig: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ tableName }) => `/User/DataTabelConfig?tabelName=${tableName}`,
        });
    },
    saveDataTableConfig: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/User/DataTabelConfig',
        });
    },
    getFiltersTagGroup: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ filtersTagGroupName }) => `User/GetFilterTags?filterTagName=${filtersTagGroupName}`,
        });
    },
    addFiltersTagGroup: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/User/AddFilterGroup',
        });
    },
    deleteFiltersTagGroup: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/User/RemoveFilterGroupByLabel',
        });
    },
    getScheduleRunDates: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ limit }) =>
                `/Schedule/GetCronFireTimes${!isNil(limit) ? combineUrlParams({ limit }) : ''}`,
        });
    },
    getPartnerMessageProviders: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Partner/GetMessageProviderList',
        });
    },
    getPartnerSiteSetting: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Partner/GetPartnerSiteSetting',
        });
    },
    updatePartnerSiteSetting: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Partner/UpdatePartnerSiteSetting',
        });
    },
    getPartnerSettings: () => {
        return getCancelableRequest<PartnerSettings>({
            requestService: CRMRequestService,
            endpointPath: () => '/PartnerSettings/Get',
        });
    },
    getPartnerCustomFields: () => {
        return postCancelableRequest<PartnerCustomField[]>({
            requestService: CRMRequestService,
            endpointPath: ({ objectType }) => `/CustomField/GetPartnerCustomFields?objectType=${objectType}`,
        });
    },
    getCustomFieldOptions: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/CustomField/GetCustomFieldOptions',
        });
    },
    addAsset: () => {
        return postCancelableRequest<DownloadFileResponse[]>({
            requestService: CRMGalleryRequestService,
            endpointPath: () => 'Save/Image',
        });
    },
    deleteObjectTag: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ id, objectType }) =>
                `/ObjectCategory/DeleteObjectTag?objectId=${id}&objectType=${objectType}`,
        });
    },
    getByPath: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ url, queryParams }) => `${url}${combineUrlParams(queryParams)}`,
        });
    },
    postByPath: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ url, queryParams }) => `${url}${combineUrlParams(queryParams)}`,
        });
    },
    getPopUpComparisons: () => {
        return getCancelableRequest<PopUpComparisonItem[]>({
            requestService: CRMRequestService,
            endpointPath: () => 'OptionsList/GetPopUpComparision',
        });
    },
    getDataById: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ url, queryParams = '' }) => {
                return `/${url}${queryParams}`;
            },
        });
    },
    getDataByIds: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ url }) => {
                return `/${url}`;
            },
        });
    },
    getMenuItemsOld: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            uriEncoding: false,
            endpointPath: ({ url }) => {
                return `?id=${url}`;
            },
        });
    },
    getPushNotificationConfig: () => {
        return getCancelableRequest<PushNotificationSettings>({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/GetPushNotificationSettings',
        });
    },
    getSiteBuilderTypes: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/SiteBuilderTypes',
        });
    },
    getPartnerState: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Partner/GetPartnerState',
        });
    },
    getNftMarketplaces: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/GetPartnerAvailableNftMarketplaces',
        });
    },
    getNftVoucherMarketplaces: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/OptionsList/GetPartnerAvailableNftVoucherMarketplaces',
        });
    },
    getSpinExchangeRatesRequest: () => {
        return getCancelableRequest<SpinExchangeRate[]>({
            requestService: CRMRequestService,
            endpointPath: ({ bonusType }) => `/PartnerSettings/GetSpinExchangeRates?bonusType=${bonusType}`,
        });
    },
    addSpinExchangeRate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ bonusType }) => `/PartnerSettings/AddSpinExchangeRate?bonusType=${bonusType}`,
        });
    },
    updateSpinExchangeRate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ bonusType }) => `/PartnerSettings/UpdateSpinExchangeRate?bonusType=${bonusType}`,
        });
    },
    deleteSpinExchangeRate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ bonusType }) => `/PartnerSettings/DeleteSpinExchangeRate?bonusType=${bonusType}`,
        });
    },
    setDefaultSpinExchangeRate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ bonusType }) => `/PartnerSettings/SetDefaultSpinExchangeRate?bonusType=${bonusType}`,
        });
    },
    getTestConfig: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/PartnerSettings/GetTestConfig',
        });
    },
    updateTestConfig: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/PartnerSettings/UpdateTestConfig',
        });
    },
    getSubscriptionPlans: () => {
        return getCancelableRequest<SubscriptionPlan[]>({
            requestService: CRMRequestService,
            endpointPath: () => '/PartnerSettings/GetSubscriptionPlans',
        });
    },
    getUserInfo: () => {
        return getCancelableRequest<UserInfo>({
            requestService: CRMRequestService,
            endpointPath: () => '/User/Info',
        });
    },
    getGlobalSearchUsers: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Search/RecentUsers',
        });
    },
    getGlobalSearchResult: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Search/Global',
        });
    },
    getPartnerCurrencies: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'OptionsList/GetPartnerCurrencies',
        });
    },
    getNftVoucherCollectionsList: () => {
        return getCancelableRequest<NFTVoucherCollectionItem[]>({
            requestService: CRMRequestService,
            endpointPath: ({ marketplace }) => `OptionsList/GetNftVoucherCollections?marketplace=${marketplace}`,
        });
    },
    getGeneralUserInfo: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ userId, pointInTime }) =>
                `/User/GetUserInfo${generateCleanUrlParams({ userId, pointInTime })}`,
        });
    },
    getUserPartners: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ userId }) => `/User/GetUserPartners?userId=${userId}`,
        });
    },
    getUserPartnerPermissions: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ userId, partnerId }) =>
                `/User/GetUserPartnerPermissions?userId=${userId}&partnerId=${partnerId}`,
        });
    },
    getCurrentUserPartnerPermissions: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ partnerId }) => `/User/GetCurrentUserPartnerPermissions?partnerId=${partnerId}`,
        });
    },
    getAvailableDeliveryMethods: () => {
        return getCancelableRequest<AvailableDeliveryMethodItem[]>({
            requestService: CRMRequestService,
            endpointPath: () => `Template/GetAvailableDeliveryMethods`,
        });
    },
};

export default UtilsHttpService;
